import React, { useState, useEffect, useRef } from 'react';
import { Navigation, Loader2 } from 'lucide-react';
import { useLocationSearch, useDetectLocation } from '@/hooks/useLocationSearch';

interface LocationInputProps {
  value: string;
  onChange: (val: string) => void;
  id?: string;
}

export function LocationInput({ value, onChange, id }: LocationInputProps) {
  const [query, setQuery] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { suggestions, isSearching } = useLocationSearch(query, isOpen);
  const { detectLocation, isLocating, errorMsg } = useDetectLocation(
    onChange, 
    (loc) => { setQuery(loc); setIsOpen(false); }
  );

  useEffect(() => {
    if (value !== query && !isOpen) {
      setQuery(value || '');
    }
  }, [value, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <input
          id={id}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => { if (query.length >= 2) setIsOpen(true); }}
          placeholder="e.g. Toronto, Canada..."
          autoComplete="off"
          className="w-full text-sm border border-slate-200 dark:border-slate-800 rounded-lg pl-3 pr-10 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 placeholder:text-slate-500 focus:border-slate-200 dark:focus:border-slate-700 focus:ring-1 focus:ring-emerald-500 outline-none"
        />
        <button
          title="Detect my location"
          aria-label="Detect my location"
          type="button"
          onClick={detectLocation}
          disabled={isLocating}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-500 disabled:opacity-50 bg-white dark:bg-slate-950 rounded-md dark:shadow-sm border border-slate-200 dark:border-slate-800 cursor-pointer disabled:cursor-not-allowed"
        >
          {isLocating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />}
        </button>
      </div>
      
      {errorMsg && (
        <div className="text-[10px] text-rose-500 font-medium absolute -bottom-5 left-1">
          {errorMsg}
        </div>
      )}

      {isOpen && query.length >= 2 && (isSearching || suggestions.length > 0) && (
        <div className="absolute z-[100] w-full mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md dark:shadow-lg overflow-hidden p-1 flex flex-col gap-1">
          {suggestions.map((sug, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-2 py-1.5 rounded-md text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
              onClick={() => {
                onChange(sug);
                setQuery(sug);
                setIsOpen(false);
              }}
            >
              {sug}
            </button>
          ))}
          
          {isSearching && (
            <div className={`px-3 py-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2 ${suggestions.length > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}`}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {suggestions.length > 0 ? 'Searching...' : 'Loading locations...'}
            </div>
          )}

          {!isSearching && suggestions.length === 0 && (
             <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                No locations found.
             </div>
          )}
        </div>
      )}
    </div>
  );
}
